import {IconButton} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ClockLine, DeleteLine} from '@dropbox/dig-icons/assets';
import {pulseUserAtom} from 'atoms/auth';
import {SavedAiView, savedAiViewsAtom} from 'atoms/savedAiViews';
import {snackbarAtom} from 'atoms/snackbar';
import {Title} from 'components/DSYS/Title';
import {isSuperUser} from 'helpers/utils';
import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {RecipeReplay} from 'views/admin/RecipeReplay';

// Keep home light: each pinned view re-runs its recipe on render, so cap the
// rows painted per table. The standalone view shows the full preview.
const HOME_PREVIEW_ROWS = 25;

const SavedAiViewCard = ({view, onRemove}: {view: SavedAiView; onRemove: () => void}) => (
  <Stack
    gap="12"
    className={atoms({
      paddingX: '20',
      paddingY: '16',
      borderRadius: 'Medium',
      borderStyle: 'Solid',
      borderColor: 'Border Subtle',
      backgroundColor: 'Background Base',
    })}
  >
    <Split alignY="center" gap="8">
      <Split.Item width="fill">
        <Title size={16}>
          {view.title}{' '}
          {view.mode === 'snapshot' && (
            <Text color="subtle" size="small">
              <UIIcon src={ClockLine} size="small" /> Snapshot
            </Text>
          )}
        </Title>
      </Split.Item>
      <Split.Item>
        <IconButton
          variant="borderless"
          size="small"
          onClick={onRemove}
          aria-label="Remove from home"
        >
          <UIIcon src={DeleteLine} />
        </IconButton>
      </Split.Item>
    </Split>

    <RecipeReplay
      encoded={view.recipe}
      mode={view.mode}
      previewRows={HOME_PREVIEW_ROWS}
      showDownload={false}
    />
  </Stack>
);

/**
 * Home shortcuts for CompanyOS data views the user pinned from the standalone
 * viewer. Renders nothing when none are saved. Each card re-runs its recipe
 * under the current user's permissions (localStorage stores only the recipe).
 */
export const SavedAiViewsSection = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const [savedViews, setSavedViews] = useAtom(savedAiViewsAtom);
  const setSnackbar = useSetAtom(snackbarAtom);

  // Only superusers can produce/replay these views today; hide otherwise.
  if (!pulseUser || !isSuperUser(pulseUser) || savedViews.length === 0) {
    return null;
  }

  const remove = (id: string) => {
    setSavedViews((prev) => prev.filter((v) => v.id !== id));
    setSnackbar({text: 'Removed from home'});
  };

  return (
    <Stack gap="16">
      {savedViews.map((view) => (
        <SavedAiViewCard key={view.id} view={view} onRemove={() => remove(view.id)} />
      ))}
    </Stack>
  );
};
